import { Text } from 'troika-three-text'
import type { GraphicsBackendKind } from '@/graphics/GraphicsConfig'
import type {
  CadTextLike,
  CadWebGpuTextMaterialLike,
  CadWebGpuTextMaterialModule,
} from '@/plugins/cad/module/CADModule/object/text/CadTextTypes'

let cadTextBackend: GraphicsBackendKind = 'webgl2'
let webGpuTextMaterialModule: CadWebGpuTextMaterialModule | null = null

/** 设置 CAD 文本对象创建使用的 backend。 */
export function setCadTextBackend(backend: GraphicsBackendKind): void {
  cadTextBackend = backend
}

/** 注册已预加载的 WebGPU 文本材质实现；null 时清理同步槽位。 */
export function registerCadWebGpuTextMaterialModule(
  module: CadWebGpuTextMaterialModule | null
): void {
  webGpuTextMaterialModule = module
}

/** 惰性加载 WebGPU TSL 文本材质，保持 WebGL2 默认产物无静态依赖。 */
export async function loadCadWebGpuTextMaterialModule(): Promise<CadWebGpuTextMaterialModule> {
  const module = await import('@/plugins/cad/module/CADModule/object/text/CadWebGpuTextMaterial')
  return module.createCadWebGpuTextMaterialModule()
}

function createWebGpuCadText(): Text {
  const module = webGpuTextMaterialModule
  if (!module) {
    throw new Error('CAD WebGPU 文本需要先 preload 注册 TSL 文本材质')
  }

  const text = new Text()
  let textMaterial: CadWebGpuTextMaterialLike | null = null
  const basePrepare = text._prepareForRender?.bind(text)

  text.createDerivedMaterial = (baseMaterial) => {
    textMaterial?.dispose()
    textMaterial = module.createTextMaterial(baseMaterial)
    return textMaterial
  }
  text._prepareForRender = (material) => {
    if (material !== textMaterial || !textMaterial) {
      basePrepare?.(material)
      return
    }
    module.ensureGlyphColorAttribute(text.geometry, text.geometry.instanceCount)
    textMaterial.syncText(text as unknown as CadTextLike)
  }
  return text
}

/** 创建与当前 backend 匹配的 troika Text；WebGL2 保持完全原行为。 */
export function createCadText(): Text {
  return cadTextBackend === 'webgpu' ? createWebGpuCadText() : new Text()
}

/** 判断材质是否为 CAD 私有 WebGPU 文本材质。 */
export function isCadWebGpuTextMaterial(value: unknown): value is CadWebGpuTextMaterialLike {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { isCadWebGpuTextMaterial?: unknown }).isCadWebGpuTextMaterial === true
  )
}
